import React, { useMemo } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import * as XLSX from 'xlsx';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import { useUserData } from '../contexts/UserDataContext';
import { useAdmin } from '../contexts/AdminContext';
import Button from '../components/Button';
import Tooltip from '../components/Tooltip';

const DashboardContainer = styled.div`
  min-height: 80vh;
  padding: ${props => props.theme.spacing.xl} 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
`;

const DashboardHeader = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const DashboardTitle = styled.h1`
  color: ${props => props.theme.colors.darkBlue};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const DashboardSubtitle = styled.p`
  font-size: 1.1rem;
  color: ${props => props.theme.colors.darkGray};
  max-width: 600px;
  margin: 0 auto;
`;

const DashboardCard = styled.div`
  background: ${props => props.theme.colors.cardBackground || props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: ${props => props.theme.spacing.xl};
  box-shadow: ${props => props.theme.shadows.lg};
  text-align: center;
`;

const CardTitle = styled.h2`
  color: ${props => props.theme.colors.darkBlue};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  justify-content: center;
  margin-top: ${props => props.theme.spacing.xl};
  flex-wrap: wrap;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const StatCard = styled.div`
  background: ${props => props.theme.colors.cardBackground || props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.lg};
  text-align: center;
  box-shadow: ${props => props.theme.shadows.md};
  border: 2px solid ${props => props.theme.colors.gray};
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: ${props => props.theme.colors.darkBlue};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.darkGray};
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ChartContainer = styled.div`
  background: ${props => props.theme.colors.cardBackground || props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.lg};
  box-shadow: ${props => props.theme.shadows.md};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const ChartTitle = styled.h3`
  color: ${props => props.theme.colors.darkBlue};
  margin-bottom: ${props => props.theme.spacing.md};
  text-align: center;
`;

const SimpleChart = styled.div`
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DashboardPage: React.FC = () => {
  const { userData, pensionResult } = useUserData();
  const { usageReports } = useAdmin();

  const downloadDetailedReport = () => {
    if (!pensionResult) {
      alert('Brak danych z symulacji. Wykonaj symulację aby pobrać raport.');
      return;
    }

    // Prepare detailed report data
    const reportData = [
      { 'Kategoria': 'TWOJA OSTATNIA SYMULACJA', 'Parametr': '', 'Wartość': '' },
      { 'Kategoria': '', 'Parametr': 'Wysokość rzeczywista', 'Wartość': `${pensionResult.actualAmount.toLocaleString('pl-PL')} PLN` },
      { 'Kategoria': '', 'Parametr': 'Oczekiwana emerytura', 'Wartość': `${userData.expectedPension.toLocaleString('pl-PL')} PLN` },
      { 'Kategoria': '', 'Parametr': 'Współczynnik zastąpienia', 'Wartość': `${pensionResult.replacementRate.toFixed(1)}%` },
      { 'Kategoria': '', 'Parametr': '', 'Wartość': '' },
      { 'Kategoria': 'STATYSTYKI OGÓLNE', 'Parametr': '', 'Wartość': '' },
      { 'Kategoria': '', 'Parametr': 'Łączna liczba symulacji', 'Wartość': stats.totalReports },
      { 'Kategoria': '', 'Parametr': 'Symulacje mężczyzn', 'Wartość': stats.maleReports },
      { 'Kategoria': '', 'Parametr': 'Symulacje kobiet', 'Wartość': stats.femaleReports },
      { 'Kategoria': '', 'Parametr': 'Średnia emerytura', 'Wartość': `${stats.avgPension.toLocaleString('pl-PL')} PLN` }
    ];

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(reportData);
    
    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Szczegółowy raport');

    // Generate filename with current date
    const filename = `raport_szczegolowy_${new Date().toLocaleDateString('pl-PL').replace(/\./g, '-')}.xlsx`;

    // Save file
    XLSX.writeFile(wb, filename);
  };

  const stats = useMemo(() => {
    const totalReports = usageReports.length;
    const maleReports = usageReports.filter(r => r.gender === 'male').length;
    const femaleReports = usageReports.filter(r => r.gender === 'female').length;
    const avgPension = usageReports.length > 0 
      ? usageReports.reduce((sum, r) => sum + r.actualPension, 0) / usageReports.length 
      : 0;
    const avgExpectedPension = usageReports.length > 0 
      ? usageReports.reduce((sum, r) => sum + r.expectedPension, 0) / usageReports.length 
      : 0;

    return {
      totalReports,
      maleReports,
      femaleReports,
      avgPension: Math.round(avgPension),
      avgExpectedPension: Math.round(avgExpectedPension)
    };
  }, [usageReports]);

  // Prepare chart data
  const chartData = useMemo(() => {
    // Gender distribution for pie chart
    const genderData = [
      { name: 'Mężczyźni', value: stats.maleReports, color: '#3F84D2' },
      { name: 'Kobiety', value: stats.femaleReports, color: '#FFB34F' }
    ];

    // Pension comparison for bar chart
    const pensionComparison = [
      { 
        name: 'Średnia oczekiwana', 
        wartość: stats.avgExpectedPension 
      },
      { 
        name: 'Średnia rzeczywista', 
        wartość: stats.avgPension 
      }
    ];

    // Age distribution - group reports by age ranges
    const ageRanges = {
      '18-30': 0,
      '31-40': 0,
      '41-50': 0,
      '51-60': 0,
      '61+': 0
    };

    usageReports.forEach(r => {
      if (r.age <= 30) ageRanges['18-30']++;
      else if (r.age <= 40) ageRanges['31-40']++;
      else if (r.age <= 50) ageRanges['41-50']++;
      else if (r.age <= 60) ageRanges['51-60']++;
      else ageRanges['61+']++;
    });

    const ageDistribution = Object.entries(ageRanges).map(([range, count]) => ({
      wiek: range,
      liczba: count
    }));

    return { genderData, pensionComparison, ageDistribution };
  }, [stats, usageReports]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <DashboardContainer>
      <Container>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <DashboardHeader>
            <motion.div variants={itemVariants}>
              <DashboardTitle>Dashboard ZUSik</DashboardTitle>
              <DashboardSubtitle>
                Zaawansowane narzędzia do szczegółowej analizy Twojej przyszłej emerytury
              </DashboardSubtitle>
            </motion.div>
          </DashboardHeader>

          <motion.div variants={itemVariants}>
            <StatsGrid>
              <StatCard>
                <StatValue>{stats.totalReports}</StatValue>
                <StatLabel>
                  Łączna liczba symulacji
                  <Tooltip text="Całkowita liczba wykonanych symulacji emerytalnych w systemie." />
                </StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>{stats.maleReports}</StatValue>
                <StatLabel>
                  Symulacje mężczyzn
                  <Tooltip text="Liczba symulacji wykonanych przez użytkowników płci męskiej." />
                </StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>{stats.femaleReports}</StatValue>
                <StatLabel>
                  Symulacje kobiet
                  <Tooltip text="Liczba symulacji wykonanych przez użytkowników płci żeńskiej." />
                </StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>{stats.avgPension.toLocaleString('pl-PL')} PLN</StatValue>
                <StatLabel>
                  Średnia emerytura
                  <Tooltip text="Średnia arytmetyczna rzeczywistych emerytur obliczonych we wszystkich symulacjach. Wzór: suma rzeczywistych emerytur / liczba symulacji." />
                </StatLabel>
              </StatCard>
            </StatsGrid>
          </motion.div>

          <motion.div variants={itemVariants}>
            <ChartContainer>
              <ChartTitle>Porównanie emerytur</ChartTitle>
              <SimpleChart>
                {stats.totalReports > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData.pensionComparison}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Bar dataKey="wartość" fill="#3F84D2" name="Emerytura (PLN)" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p>Brak danych do wyświetlenia. Wykonaj symulacje aby zobaczyć wykresy.</p>
                )}
              </SimpleChart>
            </ChartContainer>
          </motion.div>

          <motion.div variants={itemVariants}>
            <ChartContainer>
              <ChartTitle>Rozkład płci użytkowników</ChartTitle>
              <SimpleChart>
                {stats.totalReports > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={chartData.genderData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {chartData.genderData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p>Brak danych do wyświetlenia. Wykonaj symulacje aby zobaczyć wykresy.</p>
                )}
              </SimpleChart>
            </ChartContainer>
          </motion.div>

          <motion.div variants={itemVariants}>
            <ChartContainer>
              <ChartTitle>Rozkład wieku użytkowników</ChartTitle>
              <SimpleChart>
                {stats.totalReports > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData.ageDistribution}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="wiek" />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Bar dataKey="liczba" fill="#FFB34F" name="Liczba symulacji" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p>Brak danych do wyświetlenia. Wykonaj symulacje aby zobaczyć wykresy.</p>
                )}
              </SimpleChart>
            </ChartContainer>
          </motion.div>

          <motion.div variants={itemVariants}>
            <DashboardCard>
              <CardTitle>Twoja ostatnia symulacja</CardTitle>
              {pensionResult ? (
                <div>
                  <p style={{ fontSize: '1.2rem', marginBottom: '1rem' }}>
                    <strong>Oczekiwana emerytura:</strong> {userData.expectedPension.toLocaleString('pl-PL')} PLN
                  </p>
                  <p style={{ fontSize: '1.2rem', marginBottom: '1rem' }}>
                    <strong>Rzeczywista emerytura:</strong> {pensionResult.actualAmount.toLocaleString('pl-PL')} PLN
                  </p>
                  <p style={{ fontSize: '1.2rem', marginBottom: '2rem' }}>
                    <strong>Współczynnik zastąpienia:</strong> {pensionResult.replacementRate.toFixed(1)}%
                  </p>
                </div>
              ) : (
                <p>Brak danych z ostatniej symulacji. Wykonaj symulację, aby zobaczyć szczegóły.</p>
              )}
              
              <ButtonGroup>
                <Button
                  variant="outline"
                  onClick={() => window.history.back()}
                >
                  Wróć
                </Button>
                <Button
                  variant="primary"
                  onClick={downloadDetailedReport}
                >
                  Pobierz szczegółowy raport
                </Button>
              </ButtonGroup>
            </DashboardCard>
          </motion.div>
        </motion.div>
      </Container>
    </DashboardContainer>
  );
};

export default DashboardPage;
